import { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, Navigate, useLocation, useOutletContext } from 'react-router-dom';
import { useTheme } from 'next-themes';

import { clearServiceWorkerUserCaches } from '../../serviceWorkerRegistration';
import { setLogoutCallback } from '../../services/adminApi';
import { useAdminActivity } from '../../hooks';
import {
  applyAdminDisplaySize,
  cacheAdminPreferences,
  DEFAULT_ADMIN_PREFERENCES,
} from '../../services/adminPreferences';
import { SIGN_IN_GATES, closeSignInGate, gateForLogoutReason } from '../../services/adminSignInPreferences';

import AdminLogin from './AdminLogin';
import { AdminLayout } from './layout';
import ReleaseNotesUnsubscribe from './ReleaseNotesUnsubscribe';
import HeroEditor from './HeroEditor';
import ProgramsEditor from './ProgramsEditor';
import BusinessInfoEditor from './BusinessInfoEditor';
import TestimonialsEditor from './TestimonialsEditor';
import ScheduleEditor from './ScheduleEditor';
import LineageEditor from './LineageEditor';
import GalleryEditor from './GalleryEditor';
import AdminUsersEditor from './AdminUsersEditor';
import NavigationManager from './NavigationManager';
import PageBuilder from './PageBuilder';
import CustomerManagement from './CustomerManagement';
import StudentManagement from './StudentManagement';
import ResourceManager from './ResourceManager';
import BeltEditor from './BeltEditor';
import RegistrationApprovalManager from './RegistrationApprovalManager';
import TrustIndicatorsEditor from './TrustIndicatorsEditor';
import WhyOwazaEditor from './WhyOwazaEditor';
import ExpectationsEditor from './ExpectationsEditor';
import VocabularyManager from './VocabularyManager';
import QuizBuilder from './QuizBuilder';
import BadgeManager from './BadgeManager';
import ReviewQueue from './ReviewQueue';
import ScenarioManager from './ScenarioManager';
import APIKeysSettings from './APIKeysSettings';
import StripeSettings from './StripeSettings';
import PublishSettings from './PublishSettings';
import NewsletterManager from './newsletter/NewsletterManager';
import MediaLibraryManager from './MediaLibraryManager';
import QRCodeCenter from './QRCodeCenter';
import EventsManager from './EventsManager';
import FeedbackDashboard from './FeedbackDashboard';
import InboxManager from './InboxManager';
import ResourceLinksManager from './ResourceLinksManager';
import HistorySubmissionReview from './HistorySubmissionReview';
import StudentSubmissionReview from './StudentSubmissionReview';
import AnnouncementsManager from './AnnouncementsManager';
import AdminDashboardHome from './AdminDashboardHome';
import WebsiteCockpitHome from './WebsiteCockpitHome';
import { StudentsCockpitWorkspace } from './StudentsCockpitHome';
import LearningCockpitHome from './LearningCockpitHome';
import BusinessCockpitHome from './BusinessCockpitHome';
import OutreachCockpitHome from './outreach/OutreachCockpitHome';
import FinanceCockpitHome from './FinanceCockpitHome';
import SettingsCockpitHome from './SettingsCockpitHome';
import BackupsManager from './BackupsManager';
import DeploymentsManager from './DeploymentsManager';
import EmailSettings from './EmailSettings';
import {
  SocialMediaManager,
  SocialPostEditor,
  SocialPlatformSettings,
  SocialScheduleView,
  SocialFacebookHistory,
  SocialErrorBoundary,
  FacebookManager,
} from './social';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const StudentsCustomerPanel = () => {
  const { activeSubsection } = useOutletContext();
  return <CustomerManagement activeView={activeSubsection?.id} />;
};

const AdminApp = () => {
  const location = useLocation();
  const { setTheme } = useTheme();
  const setThemeRef = useRef(setTheme);

  useEffect(() => {
    setThemeRef.current = setTheme;
  }, [setTheme]);

  // Check if current route is a public route (no auth required)
  const isPublicRoute = location.pathname === '/release-notes/unsubscribe';

  // Initialize auth state from localStorage synchronously to prevent flash
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return !!localStorage.getItem('googleAuthToken');
  });
  const [loading, setLoading] = useState(() => {
    // Only show loading if there's a token to verify
    return !!localStorage.getItem('googleAuthToken');
  });
  const [logoutReason, setLogoutReason] = useState(null);

  // Clear the Owaza session and return to the login screen.
  const endSession = useCallback((reason) => {
    clearServiceWorkerUserCaches('admin');
    localStorage.removeItem('googleAuthToken');
    localStorage.removeItem('adminUser');
    applyAdminDisplaySize(DEFAULT_ADMIN_PREFERENCES.display_size);
    setThemeRef.current(DEFAULT_ADMIN_PREFERENCES.appearance);
    setIsAuthenticated(false);
    setLogoutReason(reason);
  }, []);

  // Logout handler for manual sign-out (the header/sidebar pass their click
  // event), the inactivity timer, and mid-session 401s. Every one of them
  // closes the device-local sign-in gate and disables Google auto-select so
  // the login screen never signs the user straight back in (OWZ-80).
  const handleLogout = useCallback((reason) => {
    const normalizedReason = reason === 'inactivity' || reason === 'expired' ? reason : 'signed_out';
    closeSignInGate(gateForLogoutReason(normalizedReason));
    endSession(normalizedReason);
  }, [endSession]);

  // Activity tracking for session timeout
  const {
    showWarning,
    dismissWarning,
    remainingSeconds,
    refreshSession,
  } = useAdminActivity(isAuthenticated, () => handleLogout('inactivity'));

  // Format remaining seconds as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Register the logout callback for adminApi to use on 401 errors
  useEffect(() => {
    setLogoutCallback(handleLogout);
  }, [handleLogout]);

  // Verify token function - used on mount and when page is restored from bfcache
  const verifyToken = useCallback(() => {
    const googleAuthToken = localStorage.getItem('googleAuthToken');
    if (googleAuthToken) {
      // Verify token is still valid by making a test API call
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/verify`, {
        headers: {
          'Authorization': `Bearer ${googleAuthToken}`
        }
      })
      .then(async response => {
        if (response.ok) {
          const verifiedUser = await response.json();
          const existingUser = (() => {
            try {
              return JSON.parse(localStorage.getItem('adminUser') || '{}');
            } catch {
              return {};
            }
          })();
          const mergedUser = { ...existingUser, ...verifiedUser };
          cacheAdminPreferences(verifiedUser.preferences, mergedUser);
          setThemeRef.current(verifiedUser.preferences?.appearance || 'system');
          setIsAuthenticated(true);
        } else {
          // A stored session that no longer verifies has expired or lost its
          // authorization; neither may hand straight back to Google
          // auto-select (OWZ-80).
          let detail = '';
          try {
            detail = String((await response.json())?.detail || '').toLowerCase();
          } catch {
            // No JSON body; treat as a plain expiry.
          }
          if (response.status === 403) {
            closeSignInGate(SIGN_IN_GATES.SIGNED_OUT);
            endSession('access_denied');
          } else {
            closeSignInGate(SIGN_IN_GATES.SESSION_ENDED);
            endSession(detail.includes('inactivity') ? 'inactivity' : 'expired');
          }
        }
      })
      .catch(() => {
        closeSignInGate(SIGN_IN_GATES.SESSION_ENDED);
        endSession(null);
      })
      .finally(() => {
        setLoading(false);
      });
    } else {
      setIsAuthenticated(false);
      setLoading(false);
    }
  }, [endSession]);

  // Verify on mount
  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  // Handle browser back-forward cache (bfcache)
  // When page is restored from cache, re-sync state with localStorage
  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        // Page was restored from bfcache - re-sync auth state
        const hasToken = !!localStorage.getItem('googleAuthToken');
        if (hasToken !== isAuthenticated) {
          // State is out of sync - re-verify
          if (hasToken) {
            setLoading(true);
            verifyToken();
          } else {
            setIsAuthenticated(false);
          }
        }
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [isAuthenticated, verifyToken]);

  const handleLogin = (success) => {
    setIsAuthenticated(success);
  };

  // Public routes bypass auth check
  if (isPublicRoute) {
    return (
      <Routes>
        <Route path="/release-notes/unsubscribe" element={<ReleaseNotesUnsubscribe />} />
      </Routes>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} logoutReason={logoutReason} />;
  }

  return (
    <>
      <Routes>
        {/* Admin Layout wraps all authenticated routes */}
        <Route element={<AdminLayout onLogout={handleLogout} />}>
          {/* Dashboard home */}
          <Route index element={<AdminDashboardHome />} />

          {/* Website cockpit shell remains mounted while its inline panels change. */}
          <Route element={<WebsiteCockpitHome />}>
            <Route path="website" element={null} />
            <Route path="website/hero" element={<HeroEditor />} />
            <Route path="website/trust-badges" element={<TrustIndicatorsEditor />} />
            <Route path="website/why-owaza" element={<WhyOwazaEditor />} />
            <Route path="website/expectations" element={<ExpectationsEditor />} />
            <Route path="website/programs" element={<ProgramsEditor />} />
            <Route path="website/lineage" element={<LineageEditor />} />
            <Route path="website/gallery" element={<GalleryEditor />} />
            <Route path="website/pages" element={<PageBuilder />} />
            <Route path="website/navigation" element={<NavigationManager />} />
            <Route path="website/media" element={<MediaLibraryManager />} />
            <Route path="website/announcements" element={<AnnouncementsManager />} />
            {/* Keep the legacy reviews URL inside the Website workspace. */}
            <Route path="business/reviews" element={<TestimonialsEditor />} />
          </Route>

          {/* Business cockpit shell remains mounted while its inline panels change. */}
          <Route element={<BusinessCockpitHome />}>
            <Route path="business" element={null} />
            <Route path="business/info" element={<BusinessInfoEditor />} />
            <Route path="business/schedule" element={<ScheduleEditor />} />
            <Route path="business/qr-codes" element={<QRCodeCenter />} />
          </Route>

          {/* Outreach cockpit shell remains mounted while its inline panels change. */}
          <Route element={<OutreachCockpitHome />}>
            <Route path="outreach" element={null} />
            <Route path="outreach/inbox" element={<InboxManager />} />
            <Route path="outreach/newsletter" element={<NewsletterManager />} />
            <Route path="outreach/social" element={<SocialErrorBoundary><SocialMediaManager /></SocialErrorBoundary>} />
            <Route path="outreach/social/new" element={<SocialErrorBoundary><SocialPostEditor /></SocialErrorBoundary>} />
            <Route path="outreach/social/:postId" element={<SocialErrorBoundary><SocialPostEditor /></SocialErrorBoundary>} />
            <Route path="outreach/social/schedule" element={<SocialErrorBoundary><SocialScheduleView /></SocialErrorBoundary>} />
            <Route path="outreach/social/platforms" element={<SocialErrorBoundary><SocialPlatformSettings /></SocialErrorBoundary>} />
            <Route path="outreach/social/facebook" element={<SocialErrorBoundary><SocialFacebookHistory /></SocialErrorBoundary>} />
            <Route path="outreach/social/facebook-manager" element={<SocialErrorBoundary><FacebookManager /></SocialErrorBoundary>} />
            <Route path="outreach/events" element={<EventsManager />} />
            <Route path="outreach/feedback" element={<FeedbackDashboard />} />
          </Route>

          {/* Students cockpit shell remains mounted while its inline panels change. */}
          <Route path="students" element={<StudentsCockpitWorkspace />}>
            <Route path="list" element={<StudentManagement />} />
            <Route path="customers" element={<StudentsCustomerPanel />} />
            <Route path="registrations" element={<RegistrationApprovalManager />} />
            <Route path="add-student-reviews" element={<StudentSubmissionReview />} />
            <Route path="history-reviews" element={<HistorySubmissionReview />} />
            <Route path="belts" element={<BeltEditor />} />
            <Route path="resource-links" element={<ResourceLinksManager />} />
          </Route>

          {/* Learning cockpit shell remains mounted while its inline panels change. */}
          <Route element={<LearningCockpitHome />}>
            <Route path="learning" element={null} />
            <Route path="learning/vocabulary" element={<VocabularyManager />} />
            <Route path="learning/quizzes" element={<QuizBuilder />} />
            <Route path="learning/scenarios" element={<ScenarioManager />} />
            <Route path="learning/badges" element={<BadgeManager />} />
            <Route path="learning/reviews" element={<ReviewQueue />} />
            {/* Keep the stable legacy Resources URL inside the Learning workspace. */}
            <Route path="students/resources" element={<ResourceManager />} />
          </Route>

          {/* Finance cockpit shell remains mounted while its inline panels change. */}
          <Route element={<FinanceCockpitHome />}>
            <Route path="finance" element={null} />
            {/* Keep the stable legacy Stripe URL inside the Finance workspace. */}
            <Route path="settings/stripe" element={<StripeSettings />} />
          </Route>

          {/* Settings cockpit shell remains mounted while its inline panels change. */}
          <Route element={<SettingsCockpitHome />}>
            <Route path="settings" element={null} />
            <Route path="settings/users" element={<AdminUsersEditor />} />
            <Route path="settings/api-keys" element={<APIKeysSettings />} />
            <Route path="settings/publish" element={<PublishSettings />} />
            <Route path="settings/backups" element={<BackupsManager />} />
            <Route path="settings/deployments" element={<DeploymentsManager />} />
            <Route path="settings/email" element={<EmailSettings />} />
          </Route>

          {/* Catch-all redirect to dashboard */}
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Route>
      </Routes>

      {/* Session Timeout Warning Modal */}
      <AlertDialog open={showWarning} onOpenChange={(open) => !open && dismissWarning()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Session Expiring Soon</AlertDialogTitle>
            <AlertDialogDescription>
              Your session will expire in <strong>{formatTime(remainingSeconds)}</strong> due to inactivity.
              Would you like to stay logged in?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={dismissWarning}>Dismiss</AlertDialogCancel>
            <AlertDialogAction onClick={refreshSession}>Stay Logged In</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default AdminApp;
